import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "typeSelect",
    "formatSelect",
    "japLevel",
    "prizeMoneyField",
    "homologationField",
    "dateLimite",
    "dateDebut",
    "dateFin",
    "heureDebut",
    "heureFin",
    "horairesContainer",
    "placeholder",
    "formatInfo",
    "formatRecommendation",
    "recommendationText"
  ]

  // Configuration des formats FFT (miroir de MatchFormatConfigService)
  static formatConfigs = {
    A1: { duration: 90, rest: 90, name: '3 sets à 6 jeux' },
    A2: { duration: 90, rest: 90, name: '3 sets à 6 jeux + PD' },
    B1: { duration: 75, rest: 60, name: '2 sets + super TB' },
    B2: { duration: 75, rest: 60, name: '2 sets + super TB + PD' },
    C1: { duration: 60, rest: 30, name: '2 sets à 4 jeux' },
    C2: { duration: 60, rest: 30, name: '2 sets à 4 jeux + PD' },
    D1: { duration: 45, rest: 30, name: '1 set à 9 jeux' },
    D2: { duration: 45, rest: 30, name: '1 set à 9 jeux + PD' },
    E:  { duration: 15, rest: 15, name: 'Super TB 10 pts' },
    F:  { duration: 30, rest: 15, name: '1 set à 4 jeux + PD' }
  }

  // Recommandations FFT par type de tournoi
  static recommendations = {
    P25:   { principal: 'D1', finale: 'C1', tmc: 'E' },
    P100:  { principal: 'C1', finale: 'B1', tmc: 'E' },
    P250:  { principal: 'B1', finale: 'B1', tmc: 'D1' },
    P500:  { principal: 'B1', finale: 'A1', tmc: 'D1' },
    P1000: { principal: 'B1', finale: 'A1', tmc: 'C1' },
    P1500: { principal: 'A1', finale: 'A1', tmc: 'B1' },
    P2000: { principal: 'A1', finale: 'A1', tmc: 'B1' }
  }

  connect() {
    // Mettre à jour immédiatement au chargement
    this.updateFormOptions()
    this.updateFormatInfo()
    this.updateRecommendations()

    // Forcer la mise à jour après un court délai pour s'assurer que les targets sont connectés
    setTimeout(() => {
      this.updateFormOptions()
      this.updateFormatInfo()
      this.updateRecommendations()
    }, 100)

    // Appliquer les contraintes de dates et mettre à jour les horaires
    if (this.hasDateDebutTarget && this.hasDateFinTarget) {
      this.updateDates()
    }

    // Définir le min de date_debut à aujourd'hui
    if (this.hasDateDebutTarget) {
      const today = new Date()
      this.dateDebutTarget.min = this.formatDate(today)
    }
  }

  updateFormOptions() {
    if (!this.hasTypeSelectTarget) return

    const type = this.typeSelectTarget.value

    // Afficher le niveau JAP requis
    const japLevels = {
      'P25': 'JAP1',
      'P100': 'JAP1',
      'P250': 'JAP2',
      'P500': 'JAP2',
      'P1000': 'JAP3',
      'P1500': 'JAP3',
      'P2000': 'JAP3'
    }

    if (this.hasJapLevelTarget) {
      // Si aucun type n'est sélectionné, afficher un message d'aide
      const japLevel = japLevels[type] || 'Sélectionnez un type'
      this.japLevelTarget.textContent = japLevel
      console.log('JAP Level updated:', japLevel, 'for type:', type)
    }

    // Gérer le format E (uniquement pour P25)
    if (this.hasFormatSelectTarget) {
      const formatEOption = this.formatSelectTarget.querySelector('option[value="E"]')
      if (formatEOption) {
        formatEOption.disabled = (type !== 'P25')
        if (type !== 'P25' && this.formatSelectTarget.value === 'E') {
          this.formatSelectTarget.value = 'B1' // Format par défaut
        }
      }
    }

    // Afficher/masquer le champ prize money
    if (this.hasPrizeMoneyFieldTarget) {
      const showPrizeMoney = ['P500', 'P1000', 'P1500', 'P2000'].includes(type)
      this.prizeMoneyFieldTarget.classList.toggle('hidden', !showPrizeMoney)
    }

    // Mettre à jour les recommandations FFT
    this.updateRecommendations()
  }

  toggleHomologation(event) {
    if (this.hasHomologationFieldTarget) {
      this.homologationFieldTarget.classList.toggle('hidden', !event.target.checked)
    }
  }

  toggleEchelonne(event) {
    const echelonneOptions = document.getElementById('echelonne-options')
    if (echelonneOptions) {
      echelonneOptions.classList.toggle('hidden', !event.target.checked)

      // Quand échelonné activé, pré-remplir TdS à 4 si vide
      if (event.target.checked) {
        const seedsField = echelonneOptions.querySelector('input[name*="nombre_tetes_serie"]')
        if (seedsField && (!seedsField.value || seedsField.value === '0')) {
          seedsField.value = '4'
        }
      }
    }
  }

  // Met à jour les infos du format sélectionné (durée, repos)
  updateFormatInfo() {
    if (!this.hasFormatSelectTarget || !this.hasFormatInfoTarget) return

    const format = this.formatSelectTarget.value.toUpperCase()
    const config = this.constructor.formatConfigs[format]

    if (config) {
      this.formatInfoTarget.innerHTML = `
        <span class="text-gray-600">Durée: <strong>${config.duration} min</strong></span>
        <span class="mx-2 text-gray-400">|</span>
        <span class="text-gray-600">Repos: <strong>${config.rest} min</strong></span>
      `
    }
  }

  // Met à jour les recommandations FFT en fonction du type de tournoi
  updateRecommendations() {
    if (!this.hasTypeSelectTarget || !this.hasRecommendationTextTarget) return

    const type = this.typeSelectTarget.value
    const reco = this.constructor.recommendations[type]

    if (reco) {
      this.recommendationTextTarget.innerHTML = `
        Principal: <strong>${reco.principal}</strong>,
        Finale: <strong>${reco.finale}</strong>,
        TMC: <strong>${reco.tmc}</strong>
      `
    }
  }

  updateDateLimite(event) {
    if (!this.hasDateLimiteTarget) return

    const dateDebut = event.target.value
    if (dateDebut) {
      // Définir la date limite à la veille du début par défaut
      const date = new Date(dateDebut)
      date.setDate(date.getDate() - 1)

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      // Format datetime-local: YYYY-MM-DDTHH:mm
      const defaultDateTime = `${year}-${month}-${day}T23:59`

      if (!this.dateLimiteTarget.value) {
        this.dateLimiteTarget.value = defaultDateTime
      }
    }
  }

  // ========================================
  // Gestion des horaires par jour
  // ========================================

  updateDates() {
    // 1. Enforce date_fin >= date_debut
    if (this.hasDateDebutTarget && this.hasDateFinTarget) {
      const dateDebut = this.dateDebutTarget.value
      if (dateDebut) {
        this.dateFinTarget.min = dateDebut
        const dateFin = this.dateFinTarget.value
        if (!dateFin || dateFin < dateDebut) {
          this.dateFinTarget.value = dateDebut
        }
      }
    }

    // 2. Enforce date_limite between today and date_debut
    if (this.hasDateLimiteTarget && this.hasDateDebutTarget) {
      const dateDebut = this.dateDebutTarget.value
      if (dateDebut) {
        const today = new Date()
        const todayStr = this.formatDate(today)
        this.dateLimiteTarget.min = `${todayStr}T00:00`
        this.dateLimiteTarget.max = `${dateDebut}T00:00`

        // Auto-fill: veille du tournoi 23h59
        if (!this.dateLimiteTarget.value) {
          const veille = new Date(dateDebut)
          veille.setDate(veille.getDate() - 1)
          this.dateLimiteTarget.value = `${this.formatDate(veille)}T23:59`
        }

        // Clamp if current value is out of range
        const currentVal = this.dateLimiteTarget.value
        if (currentVal && currentVal > `${dateDebut}T00:00`) {
          const veille = new Date(dateDebut)
          veille.setDate(veille.getDate() - 1)
          this.dateLimiteTarget.value = `${this.formatDate(veille)}T23:59`
        }
        if (currentVal && currentVal < `${todayStr}T00:00`) {
          this.dateLimiteTarget.value = `${todayStr}T09:00`
        }
      }
    }

    // 3. Generate horaires par jour
    if (!this.hasDateDebutTarget || !this.hasDateFinTarget || !this.hasHorairesContainerTarget) return

    const dateDebut = this.dateDebutTarget.value
    const dateFin = this.dateFinTarget.value

    if (!dateDebut || !dateFin) {
      this.showPlaceholder()
      return
    }

    const start = new Date(dateDebut)
    const end = new Date(dateFin)

    if (start > end) {
      this.showPlaceholder()
      return
    }

    // Calculate number of days
    const days = []
    const current = new Date(start)
    while (current <= end) {
      days.push(new Date(current))
      current.setDate(current.getDate() + 1)
    }

    this.generateDaySchedules(days)
  }

  generateDaySchedules(days) {
    const heureDebut = this.hasHeureDebutTarget ? this.heureDebutTarget.value || '09:00' : '09:00'
    const heureFin = this.hasHeureFinTarget ? this.heureFinTarget.value || '18:00' : '18:00'

    // Hide placeholder
    this.hidePlaceholder()

    // Generate HTML for each day
    let html = ''

    if (days.length === 1) {
      // Single day tournament - just show info
      html = `
        <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p class="text-blue-800 font-medium">Tournoi sur une journee</p>
          <p class="text-blue-600 text-sm">Les horaires par defaut seront utilises : ${heureDebut} - ${heureFin}</p>
        </div>
      `
    } else {
      // Multi-day tournament - show individual day controls
      html = `
        <div class="p-4 bg-purple-50 border border-purple-200 rounded-lg mb-4">
          <p class="text-purple-800 font-medium">Tournoi sur ${days.length} jours</p>
          <p class="text-purple-600 text-sm">Personnalisez les horaires de chaque journee si necessaire</p>
        </div>
        <div class="space-y-3">
      `

      days.forEach((day, index) => {
        const dateStr = this.formatDate(day)
        const dayName = this.getDayName(day)
        const formattedDate = this.formatDisplayDate(day)

        html += `
          <div class="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div class="flex-1">
              <span class="font-medium text-gray-900">${dayName}</span>
              <span class="text-gray-500 ml-2">${formattedDate}</span>
              ${index === 0 ? '<span class="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Jour 1</span>' : ''}
              ${index === days.length - 1 && days.length > 1 ? '<span class="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Dernier jour</span>' : ''}
            </div>
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600">Debut:</label>
              <input type="time"
                     name="horaires_par_jour[${dateStr}][heure_debut]"
                     value="${heureDebut}"
                     class="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm">
            </div>
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600">Fin:</label>
              <input type="time"
                     name="horaires_par_jour[${dateStr}][heure_fin]"
                     value="${heureFin}"
                     class="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm">
            </div>
          </div>
        `
      })

      html += '</div>'
    }

    this.horairesContainerTarget.innerHTML = html
  }

  updateDefaultTimes() {
    if (!this.hasHorairesContainerTarget) return

    // When default times change, update all day inputs if they exist
    const heureDebut = this.hasHeureDebutTarget ? this.heureDebutTarget.value : '09:00'
    const heureFin = this.hasHeureFinTarget ? this.heureFinTarget.value : '18:00'

    const debutInputs = this.horairesContainerTarget.querySelectorAll('input[name*="[heure_debut]"]')
    const finInputs = this.horairesContainerTarget.querySelectorAll('input[name*="[heure_fin]"]')

    debutInputs.forEach(input => {
      if (input.value === input.defaultValue || input.defaultValue === '') {
        input.value = heureDebut
      }
    })

    finInputs.forEach(input => {
      if (input.value === input.defaultValue || input.defaultValue === '') {
        input.value = heureFin
      }
    })
  }

  showPlaceholder() {
    if (!this.hasHorairesContainerTarget) return

    this.horairesContainerTarget.innerHTML = `
      <p class="text-gray-400 text-sm italic">
        Selectionnez les dates de debut et fin pour voir les horaires par jour
      </p>
    `
  }

  hidePlaceholder() {
    // Nothing to do, content will be replaced
  }

  formatDate(date) {
    return date.toISOString().split('T')[0]
  }

  formatDisplayDate(date) {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  getDayName(date) {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
    return days[date.getDay()]
  }
}